import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';

// ── Cheap hash-based noise (fbm only used at init, never per-frame) ────────
function hash(n: number): number {
  const x = Math.sin(n) * 43758.5453123;
  return x - Math.floor(x);
}
function noise3(x: number, y: number, z: number): number {
  return hash(x * 127.1 + y * 311.7 + z * 74.7) * 2 - 1;
}
function fbm(x: number, y: number, z: number, oct = 4): number {
  let v = 0, amp = 0.5, fr = 1;
  for (let i = 0; i < oct; i++) {
    v += noise3(x * fr, y * fr, z * fr) * amp;
    amp *= 0.5; fr *= 2.07;
  }
  return v;
}

// ── Shared mouse target (set in root, read in useFrame) ────────────────────
const mouse2 = { x: 0, y: 0 };

// ── Constants ──────────────────────────────────────────────────────────────
const ORB_COUNT   = 6200;
const FLARE_COUNT = 220;
const BASE_R      = 2.55;

// ── Main orb + surface flares ─────────────────────────────────────────────
const OrbParticles: React.FC = () => {
  const pointsRef = useRef<THREE.Points>(null);

  // Precompute per-particle static data (run once)
  const data = useMemo(() => {
    const TOTAL = ORB_COUNT + FLARE_COUNT;
    const normals    = new Float32Array(TOTAL * 3);
    const staticDisp = new Float32Array(TOTAL);
    const phaseA     = new Float32Array(TOTAL);
    const phaseB     = new Float32Array(TOTAL);
    const phaseC     = new Float32Array(TOTAL);
    const flarePhase = new Float32Array(TOTAL);
    const isFlare    = new Uint8Array(TOTAL);

    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const rng = (s: number) => hash(s * 1.618 + 0.47);

    for (let i = 0; i < TOTAL; i++) {
      const t     = i / (TOTAL - 1);
      const yUnit = 1 - t * 2;
      const ringR = Math.sqrt(Math.max(0, 1 - yUnit * yUnit));
      const phi   = goldenAngle * i;

      const nx = ringR * Math.cos(phi);
      const ny = yUnit;
      const nz = ringR * Math.sin(phi);

      normals[i * 3]     = nx;
      normals[i * 3 + 1] = ny;
      normals[i * 3 + 2] = nz;

      staticDisp[i] = fbm(nx * 2.1, ny * 2.1, nz * 2.1) * 0.38;
      phaseA[i]     = rng(i)        * Math.PI * 2;
      phaseB[i]     = rng(i + 7000) * Math.PI * 2;
      phaseC[i]     = rng(i + 3000) * Math.PI * 2;

      if (i >= ORB_COUNT) {
        isFlare[i]    = 1;
        flarePhase[i] = rng(i + 9999) * Math.PI * 2;
      }
    }

    return { normals, staticDisp, phaseA, phaseB, phaseC, flarePhase, isFlare, TOTAL };
  }, []);

  // Live-update geometry
  const geometry = useMemo(() => {
    const TOTAL = ORB_COUNT + FLARE_COUNT;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(TOTAL * 3), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(TOTAL * 3), 3).setUsage(THREE.DynamicDrawUsage));
    return geo;
  }, []);

  useFrame(({ clock }) => {
    if (!pointsRef.current) return;
    const t  = clock.getElapsedTime();
    const mx = mouse2.x;
    const my = mouse2.y;

    const { normals, staticDisp, phaseA, phaseB, phaseC, flarePhase, isFlare, TOTAL } = data;
    const pos   = geometry.attributes.position.array as Float32Array;
    const col   = geometry.attributes.color.array   as Float32Array;
    const clamp = (v: number) => v < 0 ? 0 : v > 1 ? 1 : v;

    for (let i = 0; i < TOTAL; i++) {
      const nx = normals[i * 3];
      const ny = normals[i * 3 + 1];
      const nz = normals[i * 3 + 2];

      // Layered trig waves — fast, no noise per frame
      const waveA = Math.sin(t * 0.55 + phaseA[i]) * 0.22;   // slow swell
      const waveB = Math.sin(t * 1.10 + phaseB[i]) * 0.11;   // mid ripple
      const waveC = Math.sin(t * 2.20 + phaseC[i]) * 0.045;  // micro shimmer

      // Mouse pull: face toward cursor inflates slightly
      const pull = (nx * mx + ny * my) * 0.18;

      let r = BASE_R + staticDisp[i] + waveA + waveB + waveC + pull;

      // Solar flare eruption on dedicated particles
      let flareExt = 0;
      if (isFlare[i]) {
        const lobe1 = Math.max(0, Math.sin(t * 0.44 + flarePhase[i]));
        const lobe2 = Math.max(0, Math.sin(t * 0.28 + flarePhase[i] * 1.3));
        flareExt    = lobe1 * lobe1 * lobe2 * 2.4;
        r          += flareExt;
      }

      pos[i * 3]     = nx * r;
      pos[i * 3 + 1] = ny * r;
      pos[i * 3 + 2] = nz * r;

      // Colour: pink (left) → deep blue (centre) → cyan (right)
      const ct    = (nx + 1) * 0.5;
      const rBase = clamp(ct < 0.5 ? 1.0 - ct * 0.85  : 0.08);
      const gBase = clamp(ct < 0.5 ? 0.05 + ct * 0.32 : 0.37 + (ct - 0.5) * 1.1);
      const bBase = clamp(ct < 0.5 ? 0.50 + ct * 0.50 : 1.0);

      const ridge = Math.abs(waveA + waveB) * 1.1;

      if (flareExt > 0.35) {
        const fv = Math.min(1, flareExt / 2.4);
        col[i * 3]     = clamp(rBase + fv * (1 - rBase));
        col[i * 3 + 1] = clamp(gBase + fv * (1 - gBase));
        col[i * 3 + 2] = 1.0;
      } else {
        col[i * 3]     = clamp(rBase * (0.70 + ridge));
        col[i * 3 + 1] = clamp(gBase * (0.70 + ridge));
        col[i * 3 + 2] = clamp(bBase * (0.80 + ridge));
      }
    }

    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate    = true;

    // Auto-spin + mouse lean
    pointsRef.current.rotation.y = t * 0.045 + mx * 0.28;
    pointsRef.current.rotation.x = Math.sin(t * 0.032) * 0.09 - my * 0.18;
    pointsRef.current.rotation.z = Math.cos(t * 0.025) * 0.04;
  });

  return (
    <points ref={pointsRef} geometry={geometry}>
      <pointsMaterial
        vertexColors
        transparent
        opacity={0.93}
        sizeAttenuation
        size={0.019}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
};

// ── Ejected flare trails — streaks drifting radially outward ─────────────
const FlareTrails: React.FC = () => {
  const ref   = useRef<THREE.Points>(null);
  const COUNT = 300;

  const data = useMemo(() => {
    const dir   = new Float32Array(COUNT * 3);
    const speed = new Float32Array(COUNT);
    const phase = new Float32Array(COUNT);
    const life  = new Float32Array(COUNT);

    for (let i = 0; i < COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi   = Math.acos(2 * Math.random() - 1);
      dir[i * 3]     = Math.sin(phi) * Math.cos(theta);
      dir[i * 3 + 1] = Math.cos(phi);
      dir[i * 3 + 2] = Math.sin(phi) * Math.sin(theta);
      speed[i] = 0.3 + Math.random() * 0.85;
      phase[i] = Math.random() * Math.PI * 2;
      life[i]  = 0.4 + Math.random() * 0.6;
    }
    return { dir, speed, phase, life };
  }, []);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(COUNT * 3), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(COUNT * 3), 3).setUsage(THREE.DynamicDrawUsage));
    return geo;
  }, []);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t     = clock.getElapsedTime();
    const pos   = geometry.attributes.position.array as Float32Array;
    const col   = geometry.attributes.color.array   as Float32Array;
    const { dir, speed, phase, life } = data;
    const clamp = (v: number) => v < 0 ? 0 : v > 1 ? 1 : v;

    for (let i = 0; i < COUNT; i++) {
      const raw  = (Math.sin(t * speed[i] + phase[i]) + 1) * 0.5;
      const dist = BASE_R + raw * raw * 3.6 * life[i];

      const nx = dir[i * 3];
      const ny = dir[i * 3 + 1];
      const nz = dir[i * 3 + 2];

      pos[i * 3]     = nx * dist;
      pos[i * 3 + 1] = ny * dist;
      pos[i * 3 + 2] = nz * dist;

      const a  = clamp(1 - raw * 0.9);
      const ct = (nx + 1) * 0.5;
      col[i * 3]     = clamp((ct < 0.5 ? 0.95 : 0.05)) * a;
      col[i * 3 + 1] = clamp(0.60 + nx * 0.35) * a;
      col[i * 3 + 2] = a;
    }

    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate    = true;

    // Match orb orientation
    ref.current.rotation.y = t * 0.045 + mouse2.x * 0.28;
    ref.current.rotation.x = Math.sin(t * 0.032) * 0.09 - mouse2.y * 0.18;
  });

  return (
    <points ref={ref} geometry={geometry}>
      <pointsMaterial
        vertexColors
        transparent
        opacity={0.65}
        sizeAttenuation
        size={0.012}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
};

// ── Golden orbit — warped elliptical band of drifting dust + bright beads ─
const ORBIT_A = 4.15;          // ellipse semi-axis x
const ORBIT_B = 3.40;          // ellipse semi-axis z
const ORBIT_WARP = 0.24;       // vertical bend so the ring isn't flat/circular
const ORBIT_DUST_COUNT = 850;
const ORBIT_BEAD_COUNT = 7;

const GoldenOrbit: React.FC = () => {
  const groupRef = useRef<THREE.Group>(null);

  const dust = useMemo(() => {
    const angle0   = new Float32Array(ORBIT_DUST_COUNT);
    const angSpeed = new Float32Array(ORBIT_DUST_COUNT);
    const radScale = new Float32Array(ORBIT_DUST_COUNT);
    const yScatter = new Float32Array(ORBIT_DUST_COUNT);
    const phase    = new Float32Array(ORBIT_DUST_COUNT);
    const warmth   = new Float32Array(ORBIT_DUST_COUNT);
    const rng = (s: number) => hash(s * 2.137 + 0.913);

    for (let i = 0; i < ORBIT_DUST_COUNT; i++) {
      angle0[i]   = rng(i) * Math.PI * 2;
      angSpeed[i] = 0.10 + rng(i + 1000) * 0.10;
      // static fbm wobble + band thickness keeps the path visibly imperfect
      radScale[i] = 1 + fbm(Math.cos(angle0[i]) * 1.7, rng(i + 2000), Math.sin(angle0[i]) * 1.7) * 0.16
                      + (rng(i + 3000) - 0.5) * 0.13;
      yScatter[i] = (rng(i + 4000) - 0.5) * 0.38;
      phase[i]    = rng(i + 5000) * Math.PI * 2;
      warmth[i]   = rng(i + 6000);
    }
    return { angle0, angSpeed, radScale, yScatter, phase, warmth };
  }, []);

  const beads = useMemo(() => {
    const angle0   = new Float32Array(ORBIT_BEAD_COUNT);
    const angSpeed = new Float32Array(ORBIT_BEAD_COUNT);
    const phase    = new Float32Array(ORBIT_BEAD_COUNT);
    const rng = (s: number) => hash(s * 3.71 + 0.29);
    for (let i = 0; i < ORBIT_BEAD_COUNT; i++) {
      angle0[i]   = (i / ORBIT_BEAD_COUNT) * Math.PI * 2 + rng(i) * 0.8;
      angSpeed[i] = 0.16 + rng(i + 50) * 0.10;
      phase[i]    = rng(i + 100) * Math.PI * 2;
    }
    return { angle0, angSpeed, phase };
  }, []);

  const dustGeometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(ORBIT_DUST_COUNT * 3), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(ORBIT_DUST_COUNT * 3), 3).setUsage(THREE.DynamicDrawUsage));
    return geo;
  }, []);

  const beadGeometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(ORBIT_BEAD_COUNT * 3), 3).setUsage(THREE.DynamicDrawUsage));
    return geo;
  }, []);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.getElapsedTime();
    const clamp = (v: number) => v < 0 ? 0 : v > 1 ? 1 : v;

    // Dust band
    {
      const pos = dustGeometry.attributes.position.array as Float32Array;
      const col = dustGeometry.attributes.color.array as Float32Array;
      const { angle0, angSpeed, radScale, yScatter, phase, warmth } = dust;

      for (let i = 0; i < ORBIT_DUST_COUNT; i++) {
        const theta = angle0[i] + t * angSpeed[i];
        const breathe = 1 + Math.sin(t * 0.5 + phase[i]) * 0.035;
        const s = radScale[i] * breathe;

        pos[i * 3]     = Math.cos(theta) * ORBIT_A * s;
        pos[i * 3 + 1] = yScatter[i]
                       + Math.sin(theta * 2 + 1.7) * ORBIT_WARP
                       + Math.sin(t * 0.9 + phase[i]) * 0.06;
        pos[i * 3 + 2] = Math.sin(theta) * ORBIT_B * s;

        // Warm golds: amber → pale yellow, with a soft per-particle flicker
        const flicker = 0.70 + 0.30 * Math.sin(t * 1.6 + phase[i]);
        col[i * 3]     = clamp(1.00 * flicker);
        col[i * 3 + 1] = clamp((0.62 + warmth[i] * 0.26) * flicker);
        col[i * 3 + 2] = clamp((0.08 + warmth[i] * 0.16) * flicker);
      }
      dustGeometry.attributes.position.needsUpdate = true;
      dustGeometry.attributes.color.needsUpdate = true;
    }

    // Bright beads riding the same path
    {
      const pos = beadGeometry.attributes.position.array as Float32Array;
      const { angle0, angSpeed, phase } = beads;
      for (let i = 0; i < ORBIT_BEAD_COUNT; i++) {
        const theta = angle0[i] + t * angSpeed[i];
        const s = 1 + Math.sin(theta * 3 + phase[i]) * 0.05;
        pos[i * 3]     = Math.cos(theta) * ORBIT_A * s;
        pos[i * 3 + 1] = Math.sin(theta * 2 + 1.7) * ORBIT_WARP + Math.sin(t * 0.7 + phase[i]) * 0.05;
        pos[i * 3 + 2] = Math.sin(theta) * ORBIT_B * s;
      }
      beadGeometry.attributes.position.needsUpdate = true;
    }

    // Tilted plane with slow precession + mouse lean
    groupRef.current.rotation.x = 0.55 + Math.sin(t * 0.05) * 0.05 - mouse2.y * 0.12;
    groupRef.current.rotation.z = -0.30 + Math.cos(t * 0.04) * 0.04 + mouse2.x * 0.10;
  });

  return (
    <group ref={groupRef}>
      <points geometry={dustGeometry}>
        <pointsMaterial
          vertexColors
          transparent
          opacity={0.85}
          sizeAttenuation
          size={0.016}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </points>
      <points geometry={beadGeometry}>
        <pointsMaterial
          color="#ffd980"
          transparent
          opacity={0.95}
          sizeAttenuation
          size={0.05}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </points>
    </group>
  );
};

// ── Ambient scattered dust ────────────────────────────────────────────────
const Stardust: React.FC = () => {
  const ref = useRef<THREE.Points>(null);

  const geometry = useMemo(() => {
    const COUNT = 700;
    const arr = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      const r     = 3.8 + Math.random() * 2.6;
      const theta = Math.random() * Math.PI * 2;
      const phi   = Math.acos(2 * Math.random() - 1);
      arr[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.cos(phi);
      arr[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    return geo;
  }, []);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    ref.current.rotation.y = t * 0.018 + mouse2.x * 0.12;
    ref.current.rotation.x = -mouse2.y * 0.08;
  });

  return (
    <points ref={ref} geometry={geometry}>
      <pointsMaterial
        color="#a78bfa"
        size={0.010}
        transparent
        opacity={0.32}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
};

// ── Scene ─────────────────────────────────────────────────────────────────
const Scene: React.FC = () => (
  <>
    <OrbParticles />
    <FlareTrails />
    <GoldenOrbit />
    <Stardust />
    <EffectComposer>
      <Bloom
        luminanceThreshold={0.06}
        luminanceSmoothing={0.5}
        intensity={2.6}
        radius={0.85}
      />
    </EffectComposer>
  </>
);

// ── Root — attaches mouse listener, renders canvas ────────────────────────
const ParticleOrbBackground: React.FC = () => {
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouse2.x =  (e.clientX / window.innerWidth)  * 2 - 1;
      mouse2.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        background: '#080810',
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 7.5], fov: 50 }}
        gl={{ antialias: true, alpha: false }}
        dpr={[1, 1.5]}
      >
        <Scene />
      </Canvas>
    </div>
  );
};

export default ParticleOrbBackground;
